/**
 * Gift quiz "Shopping for someone?" — Figma Quizzes › `Gift quiz · desktop` 3222:17260 /
 * `mobile` 3222:105807, result page `Gift ideas page` 3223:15493 / 3223:103416.
 *
 * Welcome → Who (saved people as cards, new-person fields under them; a new person is
 * added to People right after Who, R288) → Occasion (Other opens a text field) → Budget →
 * 07b Taste check (tuned person) or the person's Styles / Colors / Brands → Note →
 * Loading → Preview | No ideas | Error. T-135 modes: see `giftQuizSteps`.
 * Wording stays "may suit": prices and availability are not promised (R9).
 */
import type { GiftConfig } from '@/features/gift-quiz/api/gift-config'
import type { SearchV1Request } from '@/features/search/api/searchV1'
import type { Person } from '@/types/people'

export type GiftQuizStep =
  | 'welcome'
  | 'who'
  | 'occasion'
  | 'budget'
  | 'taste'
  | 'styles'
  | 'colors'
  | 'brands'
  | 'note'
  | 'loading'
  | 'preview'
  | 'empty'
  | 'error'

/**
 * How the quiz was opened (T-135). `gift` — Find a gift; `add-person` — People › Add
 * person: Who (new person only) → 08–10, Save; `tune` — Add / Update taste: 08–10b only.
 */
export type GiftQuizMode = 'gift' | 'add-person' | 'tune'

/** The question steps in order; Loading and the results come after Note. */
export const GIFT_QUIZ_STEPS: readonly GiftQuizStep[] = [
  'welcome',
  'who',
  'occasion',
  'budget',
  'styles',
  'colors',
  'brands',
  'note',
]

/**
 * 05 Occasion — Theme cards with the new line icons (Just because uses Everyday). The list and
 * labels come from `GET /gift/config` (BE-153); this set is only the icon map and the fallback
 * against an API without the route (`gift_recipients` off).
 */
export const GIFT_QUIZ_OCCASIONS = [
  { value: 'birthday', icon: 'birthday' },
  { value: 'anniversary', icon: 'anniversary' },
  { value: 'wedding', icon: 'wedding' },
  { value: 'holiday', icon: 'holiday' },
  { value: 'housewarming', icon: 'home' },
  { value: 'thank_you', icon: 'thank-you' },
  { value: 'just_because', icon: 'everyday' },
  { value: 'other', icon: 'edit' },
] as const

/** A key of `/gift/config` `occasions[]` — the static set's keys against an older API. */
export type GiftQuizOccasion = string

/**
 * 07 Budget — per gift, in the account's currency. The bands come from `GET /gift/config`
 * (`budgets[currency]`, BE-153); this set and the round scale factor are the fallback against an
 * API without the route.
 */
export const GIFT_QUIZ_BUDGETS = [
  { value: 'under_200', min: null, max: 200 },
  { value: '200_500', min: 200, max: 500 },
  { value: '500_1000', min: 500, max: 1000 },
  { value: '1000_2500', min: 1000, max: 2500 },
  { value: '2500_plus', min: 2500, max: null },
  { value: 'not_sure', min: null, max: null },
] as const

/** A key of `/gift/config` `budgets[currency][]`. */
export type GiftQuizBudget = string

const CURRENCY_SCALE: Record<string, number> = { USD: 1, EUR: 1, BYN: 3, RUB: 100 }

export function giftQuizBudgetRange(
  value: GiftQuizBudget | null,
  currency: string,
): { min: number | null; max: number | null } {
  const budget = GIFT_QUIZ_BUDGETS.find((item) => item.value === value)
  const scale = CURRENCY_SCALE[currency] ?? 1
  if (!budget) return { min: null, max: null }
  return {
    min: budget.min === null ? null : budget.min * scale,
    max: budget.max === null ? null : budget.max * scale,
  }
}

/** "Under $200", "$200–500", "$2,500+" in the shown currency. */
export function formatGiftQuizBudget(
  value: GiftQuizBudget,
  currency: string,
  labels: { under: string; notSure: string },
): string {
  if (value === 'not_sure') return labels.notSure
  const { min, max } = giftQuizBudgetRange(value, currency)
  const money = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount)
  const plain = (amount: number) => new Intl.NumberFormat('en-US').format(amount)
  if (min === null && max !== null) return `${labels.under} ${money(max)}`
  if (min !== null && max === null) return `${money(min)}+`
  if (min !== null && max !== null) return `${money(min)}–${plain(max)}`
  return labels.notSure
}

/** One card of Occasion / Budget as the quiz renders it. */
export interface GiftQuizOption {
  value: string
  label: string
  icon?: string
  min?: number | null
  max?: number | null
}

const OCCASION_ICON: Record<string, string> = Object.fromEntries(
  GIFT_QUIZ_OCCASIONS.map((option) => [option.value, option.icon]),
)

/** Occasions from the config in its order, with the design's icons (an unknown key gets Edit). */
export function giftQuizOccasionOptions(
  config: GiftConfig | null | undefined,
  fallbackLabel: (key: string) => string,
): GiftQuizOption[] {
  if (!config?.occasions?.length) {
    return GIFT_QUIZ_OCCASIONS.map((option) => ({ value: option.value, label: fallbackLabel(option.value), icon: option.icon }))
  }
  return config.occasions.map((option) => ({ value: option.key, label: option.label, icon: OCCASION_ICON[option.key] ?? 'edit' }))
}

/** The config's currency the bands are read in: the shopper's when the config has it, else its default. */
export function giftQuizBudgetCurrency(config: GiftConfig | null | undefined, currency: string): string {
  if (!config) return currency
  return config.budgets?.[currency] ? currency : config.defaultCurrency
}

/** Budget bands of the config in the shopper's currency (labels are the server's). */
export function giftQuizBudgetOptions(
  config: GiftConfig | null | undefined,
  currency: string,
  labels: { under: string; notSure: string },
): GiftQuizOption[] {
  const bands = config?.budgets?.[giftQuizBudgetCurrency(config, currency)]
  if (!bands?.length) {
    return GIFT_QUIZ_BUDGETS.map((option) => ({
      value: option.value,
      label: formatGiftQuizBudget(option.value, currency, labels),
      ...giftQuizBudgetRange(option.value, currency),
    }))
  }
  return bands.map((band) => ({ value: band.key, label: band.label, min: band.min, max: band.max }))
}

/** The recipient: a saved person or the new one typed on Who. */
export interface GiftQuizRecipient {
  /** People id once saved (a new person gets one when Continue adds them, R204). */
  id: number | null
  name: string
  /** API audience: woman / man / kids. */
  audience: 'woman' | 'man' | 'kids' | null
}

export interface GiftQuizAnswers {
  occasion: GiftQuizOccasion | null
  occasionText: string
  budget: GiftQuizBudget | null
  styleNames: string[]
  colorNames: string[]
  brandIds: number[]
  brandNames: string[]
  note: string
}

export function emptyGiftQuizAnswers(): GiftQuizAnswers {
  return {
    occasion: null,
    occasionText: '',
    budget: null,
    styleNames: [],
    colorNames: [],
    brandIds: [],
    brandNames: [],
    note: '',
  }
}

/**
 * People `gender` → search audience. A child (`girl` / `boy`, BE-150 — or the older `kids`) is
 * searched in the kids catalogue (`audience: kids`), never the adult one (audit 2026-10-19 #7).
 */
export function giftQuizAudience(gender: string | null | undefined): GiftQuizRecipient['audience'] {
  const key = gender?.trim().toLowerCase()
  if (key === 'girl' || key === 'boy' || key === 'kids') return 'kids'
  if (key === 'woman' || key === 'female') return 'woman'
  if (key === 'man' || key === 'male') return 'man'
  return null
}

/** How many ideas the search asks for; the preview shows three and "+N more ideas". */
export const GIFT_QUIZ_IDEAS_LIMIT = 20
export const GIFT_QUIZ_PREVIEW_COUNT = 3

/**
 * `POST /v1/search` for the ideas. There is no gift-ideas endpoint (BE-153 stores the person's
 * taste, not a pick): the recipient is
 * the audience, the budget the price filter, brands the brand filter; occasion, styles,
 * colors and the note go into the text query (the search's style / colour axes use their
 * own facet ids). `personalized: false` — it is someone else's taste.
 */
export function giftQuizSearchRequest(
  recipient: GiftQuizRecipient,
  answers: GiftQuizAnswers,
  budget: { min: number | null; max: number | null },
  occasionLabel: string,
): SearchV1Request {
  const words = [
    'gift',
    occasionLabel,
    answers.occasionText.trim(),
    ...answers.styleNames,
    ...answers.colorNames,
    answers.note.trim(),
  ].filter(Boolean)
  const { min, max } = budget
  const filters: SearchV1Request['filters'] = {}
  if (recipient.audience) filters.audience = [recipient.audience]
  if (min !== null || max !== null) filters.price = { ...(min !== null && { min }), ...(max !== null && { max }) }
  if (answers.brandIds.length) filters.brands = answers.brandIds.map(String)
  return {
    context: { type: 'text', query: words.join(' ') },
    filters,
    personalized: false,
    limit: GIFT_QUIZ_IDEAS_LIMIT,
  } as SearchV1Request
}

/** Decision 4 — `GET /gift/ideas` query: the person carries style / colour / brand / gender itself. */
export interface GiftIdeasParams {
  person_id: number
  occasion?: string
  occasion_text?: string
  budget_min?: number
  budget_max?: number
  limit: number
}

export function giftQuizIdeasParams(
  personId: number,
  answers: Pick<GiftQuizAnswers, 'occasion' | 'occasionText'>,
  budget: { min: number | null; max: number | null },
): GiftIdeasParams {
  const occasionText = answers.occasionText.trim()
  return {
    person_id: personId,
    ...(answers.occasion && answers.occasion !== 'other' ? { occasion: answers.occasion } : {}),
    ...(answers.occasion === 'other' && occasionText ? { occasion_text: occasionText } : {}),
    ...(budget.min !== null ? { budget_min: budget.min } : {}),
    ...(budget.max !== null ? { budget_max: budget.max } : {}),
    limit: GIFT_QUIZ_IDEAS_LIMIT,
  }
}

/** Answer chips on the preview / page: occasion, budget, then up to two styles. */
export function giftQuizAnswerChips(
  answers: GiftQuizAnswers,
  occasionLabel: string,
  budgetLabel: string,
): string[] {
  const occasion = answers.occasion === 'other' && answers.occasionText.trim() ? answers.occasionText.trim() : occasionLabel
  return [occasion, budgetLabel, ...answers.styleNames.slice(0, 2)].filter(Boolean)
}

/* ── Flow per mode (T-135) ────────────────────────────────────────────── */

export interface GiftQuizFlow {
  mode: GiftQuizMode
  /** 07b: the recipient already had taste when Budget was passed. */
  tasteCheck: boolean
  /** 07b › Change taste: 08–10 run after 07b, prefilled. */
  changeTaste: boolean
}

const TASTE_STEPS: readonly GiftQuizStep[] = ['styles', 'colors', 'brands']

/**
 * The question steps of one run. Find a gift: a tuned person gets 07b instead of 08–10
 * (Change taste brings them back); Tune only: 08–10b; Add person: Who → 08–10.
 */
export function giftQuizSteps(flow: GiftQuizFlow): GiftQuizStep[] {
  if (flow.mode === 'tune') return [...TASTE_STEPS]
  if (flow.mode === 'add-person') return ['who', ...TASTE_STEPS]
  const taste = flow.tasteCheck ? ['taste' as const, ...(flow.changeTaste ? TASTE_STEPS : [])] : TASTE_STEPS
  return ['welcome', 'who', 'occasion', 'budget', ...taste, 'note']
}

/** The neighbour step; `null` past either end (Tune only / Add person end on Save). */
export function giftQuizStepAfter(steps: readonly GiftQuizStep[], step: GiftQuizStep, by: 1 | -1): GiftQuizStep | null {
  const index = steps.indexOf(step)
  if (index < 0) return null
  return steps[index + by] ?? null
}

/** Gift: the share of questions passed (Welcome 0). No Welcome (tune / add): the first step already shows some. */
export function giftQuizProgress(step: GiftQuizStep, steps: readonly GiftQuizStep[] = GIFT_QUIZ_STEPS): number {
  const index = steps.indexOf(step)
  if (index < 0) return step === 'welcome' ? 0 : 1
  if (steps[0] !== 'welcome') return (index + 1) / (steps.length + 1)
  return index / steps.length
}

/** The person's styles / colors / brands as People stores them (BE-153). */
export interface GiftQuizPersonTaste {
  styleIds: number[]
  colorIds: number[]
  brandIds: number[]
}

/** "Prefilled from Anna's card" — the taste saved on the person (`style_ids` / `color_ids` / `brand_ids`). */
export function giftQuizPersonTaste(person: Pick<Person, 'styleIds' | 'colorIds' | 'brandIds'> | null | undefined): GiftQuizPersonTaste {
  return {
    styleIds: [...(person?.styleIds ?? [])],
    colorIds: [...(person?.colorIds ?? [])],
    brandIds: [...(person?.brandIds ?? [])],
  }
}

/** 07b subtitle: "her / his / their card" from the person's audience. */
export function giftQuizPronoun(audience: GiftQuizRecipient['audience']): 'her' | 'his' | 'their' {
  if (audience === 'woman') return 'her'
  if (audience === 'man') return 'his'
  return 'their'
}
